import "./index.css";
import { useTranslation } from "react-i18next";
import { Button, Drawer } from "@/ui-kit";
import { Form, InputControl } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";
import type { IEditUserFormData } from "../../types";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { UsersRepository } from "@/api/users/repository";
import { UsersService } from "@/api/users/service";
import { phoneSchema } from "@/utils/validation";
import { PhoneControl } from "@/components/controls/PhoneControl";
import { useAppStore } from "@/store/app/slice";
import { FileInputControl } from "@/components/controls/FileInputControl";
import { isSuperAdmin } from "@/types/users-super-admins";
import type { IUser } from "@/types/users";
import type {
  IUpdateSuperAdminDetailsRequest,
  IUpdateSuperAdminPictureInput,
  IUpdateUserDetailsRequest,
  IUpdateUserPictureInput,
} from "@/api/users/types";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IProps = {
  onDismiss: () => void;
};

export const EditUserModal = ({ onDismiss }: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const user = useAppStore((s) => s.user);

  const usersRepository = useMemo(() => {
    return new UsersRepository(new UsersService(), { abort: true });
  }, []);

  const getUserRepository = useCallback(
    (user: IUser) => {
      if (isSuperAdmin(user)) {
        return {
          updateDetails: (p: IUpdateSuperAdminDetailsRequest) =>
            usersRepository.updateSuperAdminDetails(p),
          updatePicture: (p: IUpdateSuperAdminPictureInput) =>
            usersRepository.updateSuperAdminProfilePicture(p),
        };
      }

      return {
        updateDetails: (p: IUpdateUserDetailsRequest) =>
          usersRepository.updateUserDetails(p),
        updatePicture: (p: IUpdateUserPictureInput) =>
          usersRepository.updateProfilePicture(p),
      };
    },
    [usersRepository],
  );

  const schema = useMemo(() => {
    return z
      .object({
        fullName: z.string().min(1, { message: t("validation.required") }),
        phone: phoneSchema,
        picture: z.array(z.instanceof(File)).optional(),
      })
      .loose();
  }, [t]);

  const defaultValues = useMemo((): IEditUserFormData => {
    return {
      fullName: user?.fullName || "",
      phone: user?.phone || "",
      picture: [],
      pictureUrl: user?.profilePicturePath,
    };
  }, [user]);

  const form = useForm<IEditUserFormData>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IEditUserFormData) => {
      if (!user) {
        return;
      }

      setLoading(true);

      const repository = getUserRepository(user);

      const requests: Promise<unknown>[] = [
        repository.updateDetails({
          id: user.id,
          fullName: formData.fullName,
          phone: formData.phone,
        }),
      ];

      if (formData.picture && formData.picture.length > 0) {
        const picture = {
          userId: user.id,
          picture: formData.picture[0],
        };

        requests.push(repository.updatePicture(picture));
      }

      Promise.all(requests)
        .then(() => {
          usersRepository.getCurrentUser().then((response) => {
            useAppStore.getState().setUser(response);
          });
          onDismiss();
        })
        .catch(serverErrorHandler())
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, onDismiss, user, getUserRepository, usersRepository]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  return (
    <FormProvider {...form}>
      <Drawer
        className="settings__user-modal"
        title={t("action.edit_what", {
          what: t("label.profile").toLowerCase(),
        })}
        open={true}
        onOpenChange={onDismiss}
        closeOnOutsidePress={false}
        footer={
          <>
            <Button onClick={handleDismiss} variant="ghost" color="layer1">
              {t("action.cancel")}
            </Button>
            <Button onClick={onSubmit} loading={loading}>
              {t("action.save")}
            </Button>
          </>
        }
      >
        <Form>
          <FileInputControl label={t("label.profile_picture")} name="picture" />
          <InputControl label={t("label.full_name")} name="fullName" />
          <PhoneControl name="phone" />
        </Form>
      </Drawer>
    </FormProvider>
  );
};
